'use client';

import React, { useState } from 'react';
import { extractComponentFromChunk } from '@/ui/utils/tiles';
import { buildTrackingId } from '@/utils/tracking-helper';
import { useCurrencyFormatter } from '@/ui/hooks/useCurrencyFormatter';
import { usePathname } from 'next/navigation';
import {
  BundleItemDetailsFragment,
  ContentChunkFragment,
  ItemRelationsFragment,
  PieceFragment,
} from '@/integrations/crystallize/__gql-codegen-types/catalogue/graphql';
import { ArrayElementType } from '@/integrations/crystallize/types/helpers/ArrayElementType';
import { DocShapeId } from '@/utils/shapeIds';
import {
  useExternalAreaLink,
  useExternalAreaLinkDismissed,
} from '@/ui/providers/externalAreaLinkProvider';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { findExternalAreaLinkForArea } from '@/ui/utils/externalAreaLink';
import { buildQueryStringFromPropertiesTable } from '@/utils/url-helper';
import { componentByTypename } from '@/ui/utils/componentByTypename';
import { getDefaultOrFirstVariant } from '@/integrations/crystallize/helpers/product-variant-helper';
import { ButtonVariants } from '../Button/types/ButtonVariants';
import ShippingAvailabilityModal from '../ShippingAvailabilityModal';

import { SharedButtonLinkProps } from './types';
import LinkButton from './link-button';
import { BundleButton } from './bundle-button';
import ProductVariantButton from './product-variant-button';
import ExternalAreaButton from './external-area-button';
import { CopyVoucherButton } from './copy-voucher-button';

export interface ButtonLinkProps {
  ctaComponent: PieceFragment | null | undefined;
  className?: string;
  applyClassesToButton?: boolean;
  variant?: 'primary' | 'secondary' | 'tertiary';
  children?: React.ReactNode;
  onClick?: () => void;
  'aria-label'?: string;
  trackingPrefix?: string;
}

export default function ButtonLink({
  ctaComponent,
  className,
  applyClassesToButton,
  variant,
  children,
  onClick,
  'aria-label': ariaLabel,
  trackingPrefix,
}: Readonly<ButtonLinkProps>) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { formatCurrency } = useCurrencyFormatter();
  const currentPath = usePathname();
  const { areaVariables, country, locale } = useLocalization();
  const contextExternalAreaLink = useExternalAreaLink();
  const { isDismissed, dismiss } = useExternalAreaLinkDismissed();

  const components = ctaComponent?.components;
  if (!components) {
    return null;
  }

  // A voucher-code CTA copies the code instead of navigating. It renders as a normal Button using
  // the CTA's own `variant`, so it looks like the campaign's other buttons.
  const voucherCode = extractComponentFromChunk(
    components,
    'voucher-code',
    'SingleLineContent',
  )?.text;

  if (voucherCode) {
    const voucherVariant = extractComponentFromChunk(components, 'variant', 'SelectionContent')
      ?.options?.[0]?.value as ButtonVariants | undefined;

    return <CopyVoucherButton code={voucherCode} variant={voucherVariant} className={className} />;
  }

  const text = extractComponentFromChunk(components, 'text', 'SingleLineContent')?.text;
  if (!text) {
    return null;
  }

  // Extract external area link from the CTA piece's own components
  const externalAreaLinkChunk = extractComponentFromChunk(
    components,
    'external-area-link',
    'ContentChunkContent',
  ) as ContentChunkFragment | undefined;
  const ctaExternalAreaLink = findExternalAreaLinkForArea(externalAreaLinkChunk, areaVariables.id);
  const effectiveExternalAreaLink = isDismissed
    ? null
    : (ctaExternalAreaLink ?? contextExternalAreaLink);

  const buttonVariant = extractComponentFromChunk(components, 'variant', 'SelectionContent')
    ?.options?.[0]?.value;
  const noUppercase = extractComponentFromChunk(
    components,
    'no-uppercase',
    'BooleanContent',
  )?.value;
  const finalButtonVariant = variant || (buttonVariant as ButtonVariants | undefined);

  const internalLinkRelation = extractComponentFromChunk(
    components,
    'internal-link',
    'ItemRelationsContent',
  );
  const internalLinkItem = internalLinkRelation?.items?.find(
    (item) => item.shape?.identifier !== DocShapeId.bundle,
  );
  const internalLinkProduct = componentByTypename(internalLinkItem, 'Product');
  const internalLinkPriceVariant = getDefaultOrFirstVariant(
    internalLinkProduct?.variants,
  )?.priceVariant;
  const internalLink = internalLinkItem?.path;
  const anchorTile = extractComponentFromChunk(components, 'anchor-tile', 'ItemRelationsContent')
    ?.items?.[0];
  const productVariant = internalLinkRelation?.productVariants?.[0];
  const bundleItem = internalLinkRelation?.items?.find(
    (item) => item.shape?.identifier === DocShapeId.bundle,
  );
  const externalLink = extractComponentFromChunk(
    components,
    'external-link',
    'SingleLineContent',
  )?.text;
  const fallbackLink = extractComponentFromChunk(
    components,
    'fallback-link',
    'SingleLineContent',
  )?.text;
  const openInNewTab = extractComponentFromChunk(
    components,
    'open-link-in-new-tab',
    'BooleanContent',
  )?.value;
  const addPlayIcon = extractComponentFromChunk(
    components,
    'add-play-icon',
    'BooleanContent',
  )?.value;
  const pricePosition = extractComponentFromChunk(components, 'price-position', 'SelectionContent')
    ?.options?.[0]?.value;

  const urlParamsSections = extractComponentFromChunk(
    components,
    'url-parameters',
    'PropertiesTableContent',
  )?.sections;
  const urlParamsQueryString = buildQueryStringFromPropertiesTable(urlParamsSections);

  const modal = effectiveExternalAreaLink && (
    <ShippingAvailabilityModal
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
      onTryAnyway={() => {
        setIsModalOpen(false);
        setTimeout(dismiss, 0);
      }}
      externalLink={effectiveExternalAreaLink}
    />
  );

  // Resolved once and forwarded to whichever branch renders. When an external
  // area link applies, the button opens the modal instead of navigating.
  const hasExternalAreaLink = Boolean(effectiveExternalAreaLink);
  const sharedProps: SharedButtonLinkProps = {
    text,
    children,
    variant: finalButtonVariant,
    noUppercase,
    ariaLabel,
    tileElementTrackingId: buildTrackingId(trackingPrefix, text),
    hasExternalAreaLink,
    onClick: hasExternalAreaLink ? () => setIsModalOpen(true) : onClick,
    modal,
  };

  if (internalLink || externalLink) {
    return (
      <LinkButton
        {...sharedProps}
        internalLink={internalLink}
        externalLink={externalLink}
        fallbackLink={fallbackLink}
        destinationTopics={internalLinkItem?.topics}
        anchorTile={anchorTile as ArrayElementType<ItemRelationsFragment['items']> | undefined}
        country={country}
        locale={locale}
        openInNewTab={openInNewTab}
        addPlayIcon={addPlayIcon}
        className={className}
        urlParamsQueryString={urlParamsQueryString}
        pricePosition={pricePosition}
        priceVariant={internalLinkPriceVariant}
        applyClassesToButton={applyClassesToButton}
        formatCurrency={formatCurrency}
      />
    );
  }

  if (bundleItem) {
    return (
      <BundleButton
        {...sharedProps}
        bundleItem={bundleItem as BundleItemDetailsFragment}
        currentPath={currentPath}
        pricePosition={pricePosition}
        openInNewTab={openInNewTab}
        className={className}
        applyClassesToButton={applyClassesToButton}
        urlParamsQueryString={urlParamsQueryString}
        formatCurrency={formatCurrency}
      />
    );
  }

  if (productVariant) {
    return (
      <ProductVariantButton
        {...sharedProps}
        productVariant={productVariant}
        pricePosition={pricePosition}
        currentPath={currentPath}
        openInNewTab={openInNewTab}
        className={className}
        applyClassesToButton={applyClassesToButton}
        urlParamsQueryString={urlParamsQueryString}
        formatCurrency={formatCurrency}
      />
    );
  }

  if (effectiveExternalAreaLink) {
    return <ExternalAreaButton {...sharedProps} className={className} />;
  }

  return null;
}
